import type { ComponentType, ReactNode } from "react"

import {
  Field,
  FieldError,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@atlant/ui"

interface FormSelectOption {
  value: string
  label: string
  icon?: ReactNode | ComponentType<{ className?: string }>
}

interface FormSelectProps {
  value: string | undefined
  onChange: (value: string | null) => void
  name?: string
  label?: string
  placeholder?: string
  options: FormSelectOption[]
  disabled?: boolean
  className?: string
  icon?: ReactNode
  error?: string
  required?: boolean
}

export const FormSelect = ({
  value,
  onChange,
  name,
  label,
  placeholder = "Select an option",
  options,
  disabled,
  className,
  icon,
  error,
  required,
}: FormSelectProps) => {
  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel
          htmlFor={id}
          className="text-muted-foreground gap-1 text-sm font-medium"
        >
          {label}
          {required && <span className="text-xs text-red-500">*</span>}
        </FieldLabel>
      )}
      <Select
        key={value}
        onValueChange={onChange}
        value={value || ""}
        disabled={disabled}
      >
        {icon ? (
          <div className="relative">
            <div className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2">
              {icon}
            </div>
            <SelectTrigger
              id={id}
              className="hover:bg-input/10 border-input m-0 w-full pl-8 data-[size=default]:h-10"
            >
              <SelectValue
                placeholder={<span className="text-placeholder">{placeholder}</span>}
              />
            </SelectTrigger>
          </div>
        ) : (
          <SelectTrigger
            id={id}
            className="hover:bg-input/10 border-input m-0 h-10 w-full data-[size=default]:h-10"
          >
            <SelectValue
              placeholder={<span className="text-placeholder">{placeholder}</span>}
            />
          </SelectTrigger>
        )}
        <SelectContent className="max-h-50">
          {options.map((option) => {
            const Icon = option.icon
            const iconNode = Icon ? (
              typeof Icon === "function" ? (
                <Icon className="size-4" />
              ) : (
                Icon
              )
            ) : null

            return (
              <SelectItem key={option.value} value={option.value}>
                {iconNode && <span className="text-muted-foreground">{iconNode}</span>}
                {option.label}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
